import { useEffect } from 'react'
import {
  Room,
  RoomEvent,
  Track,
  type LocalTrackPublication,
  type RemoteParticipant,
} from 'livekit-client'

import { clientLogError, clientLogInfo } from '@/src/lib/clientLog'
import {
  CODEC_SWITCH_COMPLAINT_WINDOW_MS,
  CODEC_SWITCH_COOLDOWN_MS,
  MAX_CODEC_SWITCHES_PER_TRACK,
  SCREEN_SHARE_CODEC_TOPIC,
  countLiveScreenShareViewers,
  decodeScreenShareCodecMessage,
  encodeScreenShareCodecMessage,
  noteCodecSwitchSettled,
  noteCodecSwitchStarting,
  republishScreenShareAsVp8,
  requiredComplaints,
} from '@/src/lib/screenShareCodec'

type SwitchRecord = {
  count: number
  lastAt: number | null
}

export type UseScreenShareCodecSwitchOptions = {
  room?: Room
  enabled?: boolean
}

/**
 * Принимает жалобы зрителей и тихо переводит свой экран на VP8.
 *
 * Порог жалоб зависит от числа живых зрителей трека: маленькая комната чинится
 * по первой жалобе, большая — только по двум от разных зрителей. Сам переход —
 * переиздание того же `MediaStreamTrack`, диалог выбора окна не открывается,
 * раскладка зрителей прикрыта заморозкой автопина.
 *
 * @param options.room - комната livekit, пока undefined хук неактивен
 * @param options.enabled - выключатель, по умолчанию включён
 */
export function useScreenShareCodecSwitch({
  room,
  enabled = true,
}: UseScreenShareCodecSwitchOptions): void {
  useEffect(() => {
    if (!room || !enabled) {
      return
    }

    // Жалобы по трекам: sid демонстрации -> зритель -> время жалобы.
    const complaints = new Map<string, Map<string, number>>()
    // Счётчик переходов: sid демонстрации -> попытки и время последней.
    const switches = new Map<string, SwitchRecord>()

    /**
     * Рассылает итог перехода всем: жалобы по старому треку прекращаются.
     */
    const broadcastSwitched = async (message: {
      oldTrackSid: string
      newTrackSid: string | null
      publisherIdentity: string
      complaintsCount: number
      ok: boolean
    }): Promise<void> => {
      await room.localParticipant.publishData(
        encodeScreenShareCodecMessage({
          type: 'SCREEN_SHARE_CODEC_SWITCHED',
          oldTrackSid: message.oldTrackSid,
          newTrackSid: message.newTrackSid,
          publisherIdentity: message.publisherIdentity,
          codecFrom: 'h264',
          codecTo: 'vp8',
          complaintsCount: message.complaintsCount,
          ok: message.ok,
        }),
        { reliable: true, topic: SCREEN_SHARE_CODEC_TOPIC },
      )
    }

    /**
     * Выполняет переход: предупреждение, переиздание трека и итог всем.
     */
    const runSwitch = async (trackSid: string, complaintsCount: number): Promise<void> => {
      const identity = room.localParticipant.identity
      const now = Date.now()
      const record = switches.get(trackSid) ?? { count: 0, lastAt: null }

      if (record.count >= MAX_CODEC_SWITCHES_PER_TRACK) {
        return
      }

      if (record.lastAt !== null && now - record.lastAt < CODEC_SWITCH_COOLDOWN_MS) {
        return
      }

      record.count += 1
      record.lastAt = now
      switches.set(trackSid, record)

      noteCodecSwitchStarting(identity)

      try {
        await room.localParticipant.publishData(
          encodeScreenShareCodecMessage({
            type: 'SCREEN_SHARE_CODEC_SWITCHING',
            oldTrackSid: trackSid,
            publisherIdentity: identity,
          }),
          { reliable: true, topic: SCREEN_SHARE_CODEC_TOPIC },
        )
      } catch {
        // Предупреждение не ушло, а переход всё равно нужен — идём дальше
      }

      clientLogInfo('screen-share-codec-switch-start', {
        room: room.name,
        trackSid,
        publisher: identity,
        complaintsCount,
        attempt: record.count,
      })

      try {
        const result = await republishScreenShareAsVp8(room)

        if (!result) {
          throw new Error('Нет живого видеотрека для переиздания в VP8')
        }

        await broadcastSwitched({
          oldTrackSid: result.oldTrackSid,
          newTrackSid: result.newTrackSid,
          publisherIdentity: identity,
          complaintsCount,
          ok: true,
        })

        clientLogInfo('screen-share-codec-switch-done', {
          room: room.name,
          oldTrackSid: result.oldTrackSid,
          newTrackSid: result.newTrackSid,
          publisher: identity,
          complaintsCount,
          codecFrom: 'h264',
          codecTo: 'vp8',
        })
      } catch (error) {
        clientLogError('screen-share-codec-switch-failed', {
          room: room.name,
          trackSid,
          publisher: identity,
          error: error instanceof Error ? error.message : String(error),
        })

        try {
          await broadcastSwitched({
            oldTrackSid: trackSid,
            newTrackSid: null,
            publisherIdentity: identity,
            complaintsCount,
            ok: false,
          })
        } catch {
          // Итог не разослать — зрители отработают по таймауту заморозки пина
        }
      } finally {
        noteCodecSwitchSettled(identity)
      }
    }

    /**
     * Копит жалобы и запускает переход, когда хватает голосов.
     */
    const handleDataReceived = (payload: Uint8Array, participant?: RemoteParticipant): void => {
      const message = decodeScreenShareCodecMessage(payload)

      if (!message || message.type !== 'SCREEN_SHARE_CODEC_COMPLAINT') {
        return
      }

      const myPublication = room.localParticipant.getTrackPublication(Track.Source.ScreenShare)

      // Чужая демонстрация или устаревший трек — жалоба не нам.
      if (!myPublication || myPublication.trackSid !== message.trackSid) {
        return
      }

      if (!participant || participant.identity === room.localParticipant.identity) {
        return
      }

      const now = Date.now()
      let byViewer = complaints.get(message.trackSid)

      if (!byViewer) {
        byViewer = new Map()
        complaints.set(message.trackSid, byViewer)
      }

      byViewer.set(participant.identity, now)

      byViewer.forEach((sentAt, viewer) => {
        if (now - sentAt > CODEC_SWITCH_COMPLAINT_WINDOW_MS) {
          byViewer?.delete(viewer)
        }
      })

      const viewerCount = countLiveScreenShareViewers(room, message.trackSid)

      clientLogInfo('screen-share-codec-complaint-received', {
        room: room.name,
        trackSid: message.trackSid,
        viewer: participant.identity,
        distinctComplaints: byViewer.size,
        requiredComplaints: requiredComplaints(viewerCount),
        viewerCount,
      })

      if (byViewer.size < requiredComplaints(viewerCount)) {
        return
      }

      complaints.delete(message.trackSid)

      void runSwitch(message.trackSid, byViewer.size)
    }

    /**
     * Новая демонстрация обнуляет старые жалобы: голоса были про другой трек.
     */
    const handleLocalTrackPublished = (publication: LocalTrackPublication): void => {
      if (publication.source === Track.Source.ScreenShare) {
        complaints.clear()
      }
    }

    room
      .on(RoomEvent.DataReceived, handleDataReceived)
      .on(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)

    return () => {
      room
        .off(RoomEvent.DataReceived, handleDataReceived)
        .off(RoomEvent.LocalTrackPublished, handleLocalTrackPublished)

      complaints.clear()
    }
  }, [room, enabled])
}
